import { put, take, select, takeLatest, takeEvery, all, call } from 'redux-saga/effects';
import ktRequest from '../../../utils/request';
import types from './types';
import actions from './actions';
import moment from 'moment';
import { groupBy } from 'lodash';
import { splitTimeOffRangeRequests } from '../../../utils/timeOffRequests';

const getCenterId = state => state.identity.centerId;

function * fetchTimeOffRequests (payload) {
  try {
    const { start, end } = payload;
    let centerId = yield select(getCenterId);
    const formattedStart = start ? moment(start).format('YYYY-MM-DD') : '';
    const formattedEnd = end ? moment(end).format('YYYY-MM-DD') : '';

    if (!centerId) {
      yield take('FETCH_PROFILE_SUCCESS');
    }

    // get updated center id
    centerId = yield select(getCenterId);

    const response = yield ktRequest.get(`/time-off/requests?centerId=${centerId}&start=${formattedStart}&end=${formattedEnd}`, 'timecards');
    const { data } = response;

    const groupedByEmployeeId = groupBy(data, 'employeeId');

    /**
     * create a new array for the time off requests
     * - if a time off request is a range, we split that single request into single day requests for the front end
     * (ex: 9/16 - 9/19) will be split into 4 single day requests where the only difference between them are the start and end dates
     * - if a time off request is a single day, it is simply added to the array
     */
    Object.keys(groupedByEmployeeId).forEach(employeeId => {
      const currentValue = groupedByEmployeeId[employeeId];
      const timeOffRequests = splitTimeOffRangeRequests(currentValue);

      groupedByEmployeeId[employeeId] = timeOffRequests;
    });

    yield put(actions.getTimeOffRequestsSucces(data, groupedByEmployeeId));
  } catch (error) {
    yield put(actions.getTimeOffRequestsFailure());
  }
}

function * updateTimeOffRequestStatus (payload) {
  try {
    const { requests } = payload;
    const centerId = yield select(getCenterId);
    const body = {
      centerId,
      requests
    };

    const response = yield ktRequest.post(`/time-off/update-request-status`, body, 'timecards');

    if (!response.ok) {
      const json = yield response.json();
      const { error } = json;
      throw new Error(error);
    }

    yield put(actions.updateTimeOffRequestStatusSuccess(requests));
  } catch (error) {
    console.log(error);
    yield put(actions.updateTimeOffRequestStatusFailure());
  }
}

function * editRequest (payload) {
  try {
    const { id, requestType, timeOffType, start, end, description } = payload;
    const centerId = yield select(getCenterId);
    const startTimeISO = moment(start).toISOString();
    const endTimeISO = moment(end).toISOString();
    const body = {
      id,
      requestType,
      timeOffType,
      startTime: startTimeISO,
      endTime: endTimeISO,
      description,
      centerId
    };

    const response = yield ktRequest.post(`/time-off/request/${id}/update`, body, 'timecards');

    if (!response.ok) {
      const json = yield response.json();
      throw new Error(json.error);
    }

    yield put(actions.editRequestSuccess(id, requestType, timeOffType, start, end, description));
  } catch (error) {
    yield put(actions.editRequestFailure(error));
  }
}

function * archiveRequest (payload) {
  try {
    const { ids } = payload;
    const centerId = yield select(getCenterId);

    yield all(ids.map(id => call(function * () {
      try {
        const body = { id, centerId };
        const response = yield ktRequest.post(`/time-off/request/${id}/archive`, body, 'timecards');

        if (!response.ok) {
          const json = yield response.json();
          throw new Error(json.error);
        }
      } catch (error) {
        console.log(error);
      }
    })));

    yield put(actions.archiveTimeOffRequestSuccess(ids));
  } catch (error) {
    yield put(actions.archiveTimeOffRequestFailure(error));
  }
}

function * managerEmployeeTimeOffRequest (payload) {
  try {
    const { request } = payload;
    const centerId = yield select(getCenterId);
    const body = {
      ...request,
      startTime: request.startDateTime,
      endTime: request.endDateTime,
      employeeId: request.employee.id,
      allDay: request.requestType === 'allDay',
      centerId
    };

    // passing the first item to the reducer because we only receive one item from the endpoint
    const response = yield ktRequest.post('/time-off/create-time-off-for-employee', body, 'timecards');

    if (!response.ok) {
      throw new Error('Something went wrong.');
    }

    const json = yield response.json();
    const { data } = json;

    yield put(actions.managerCreateEmployeeTimeOffSuccess(data[0]));
  } catch (error) {
    yield put(actions.managerCreateEmployeeTimeOffFailure(error));
  }
}

export const timeOffRequestSagas = [
  takeLatest(types.GET_TIME_OFF_REQUESTS_REQUEST, fetchTimeOffRequests),
  takeEvery(types.UPDATE_TIME_OFF_REQUEST_STATUS_REQUEST, updateTimeOffRequestStatus),
  takeLatest(types.EDIT_TIME_OFF_REQUEST_REQUEST, editRequest),
  takeLatest(types.ARCHIVE_TIME_OFF_REQUEST_REQUEST, archiveRequest),
  takeLatest(types.MANAGER_CREATE_EMPLOYEE_TIME_OFF_REQUEST_REQUEST, managerEmployeeTimeOffRequest)
];
